<script>

import ImpressionModels from "@/views/Impression/Components/ImpressionModels.vue";
import {onMounted, ref} from "vue";
import axios from "axios";
import useTools from "@/use/Tools";
import PaySlipFilter from "@/views/PaySlip/Components/PaySlipFilter.vue";
import useSharedMethod from "@/use/SharedMethod";
import useSharedData from "@/use/SharedData";

export default {
    components: {PaySlipFilter, ImpressionModels},
    setup() {
        const {$moment} = useTools();
        const {transformFilterValues} = useSharedMethod();
        const {filter} = useSharedData();

        filter.value = {
            closed: null,
        };

        function clearFilter() {
            Object.keys(filter.value).forEach(key => {
                filter.value[key] = null;
            });
        }

        const cnasAgencyId = ref(null);
        const year = ref($moment().year() - 1);
        const typeDeclaration = ref(1);
        const imprimerRecap = ref(true);
        const imprimerDas = ref(true);
        const certifieExacteA = ref(null);
        const certifieExacteLe = ref($moment().format('YYYY-MM-DD'));


        const loading = ref(false);
        const documentUrl = ref(null);

        function print() {
            loading.value = true;
            documentUrl.value = null;
            axios.get(`/printed/getDAS`, {
                responseType: 'blob',
                params: transformFilterValues({
                    ...filter.value,
                    cnasAgencyId: cnasAgencyId.value,
                    year: year.value,
                    typeDeclaration: typeDeclaration.value,
                    imprimerRecap: imprimerRecap.value,
                    imprimerDas: imprimerDas.value,
                    certifieExacteA: certifieExacteA.value,
                    certifieExacteLe: certifieExacteLe.value,
                })
            }).then(response => {
                const blob = response.data;
                documentUrl.value = window.URL.createObjectURL(blob);
            }).finally(() => {
                loading.value = false;
            })
        }

        const downloadLoading = ref(false);

        function download() {
            downloadLoading.value = true;
            axios.get(`/printed/getDASFile`, {
                responseType: 'blob',
                params: transformFilterValues({
                    ...filter.value,
                    cnasAgencyId: cnasAgencyId.value,
                    year: year.value,
                })
            }).then((response) => {
                const contentDisposition = response.headers['content-disposition'];
                let filename;

                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                    filename = filenameMatch && filenameMatch[1] ? filenameMatch[1] : 'default.zip';
                } else {
                    filename = `DAS_${year.value}.zip`;
                }

                const blob = new Blob([response.data], {type: 'application/zip'});
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();

                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }).finally(() => {
                downloadLoading.value = false;
            });
        }

        onMounted(() => print());

        return {
            cnasAgencyId,
            year,
            typeDeclaration,
            imprimerRecap,
            imprimerDas,
            certifieExacteA,
            certifieExacteLe,
            loading,
            documentUrl,
            downloadLoading,
            filter,
            clearFilter,
            print,
            download
        }
    }
}
</script>

<template>
    <div>
        <w-app
            :title="$t('module.cnas_declaration_annuelle')"
            show-filter
            :filter="filter"
            @clearFilter="clearFilter"
        >

            <template v-slot:filter>
                <pay-slip-filter v-model="filter"/>
            </template>

            <template v-slot:leftToolbar></template>
            <template v-slot:rightToolbar>
                <impression-models/>
                <w-btn
                    icon="mdi-monitor-arrow-down"
                    color="teal"
                    :text="$t('common.download')"
                    :loading="loading"
                    @press="print"
                />
            </template>
            <template v-slot:content>
                <v-card>
                    <v-container fluid>
                        <v-row dense>
                            <v-col cols="12" md="5">
                                <w-fieldset title="Détails">
                                    <v-row>
                                        <v-col cols="12">
                                            <str-cnas-agcency-autocomplete
                                                v-model="cnasAgencyId"
                                                :label="$t('module.cnas_agency')"
                                                not-clearable
                                                set-default
                                            />
                                        </v-col>
                                        <v-col cols="6">
                                            <w-text-field
                                                v-model="year"
                                                :label="$t('common.year')"
                                                type="number"
                                            />
                                        </v-col>
                                        <v-col cols="6">
                                            <w-enum-select
                                                v-model="typeDeclaration"
                                                :items="[
                                                   {id: 1, name: $t('impression.type_declaration_normal')},
                                                   {id: 2, name: $t('impression.type_declaration_recovery')},
                                                   {id: 3, name: $t('impression.type_declaration_complementary')},
                                                ]"
                                                :label="$t('impression.type_declaration')"
                                            />
                                        </v-col>
                                        <v-col cols="6">
                                            <w-text-field
                                                v-model="certifieExacteA"
                                                :label="$t('impression.certified_accurate_to')"
                                            />
                                        </v-col>
                                        <v-col cols="6">
                                            <w-date-picker
                                                v-model="certifieExacteLe"
                                                :label="$t('impression.certified_accurate')"
                                            />
                                        </v-col>
                                        <v-col cols="6">
                                            <v-checkbox
                                                v-model="imprimerRecap"
                                                :label="$t('impression.print_recap')"
                                                hide-details
                                            />
                                        </v-col>
                                        <v-col cols="6">
                                            <v-checkbox
                                                v-model="imprimerDas"
                                                :label="$t('impression.print_das')"
                                                hide-details
                                            />
                                        </v-col>
                                        <v-col cols="12">
                                            <w-btn
                                                icon="mdi-download"
                                                color="teal"
                                                block
                                                :text="$t('impression.cnas_generate_two_file')"
                                                :loading="loading"
                                                @press="download"
                                            />
                                        </v-col>
                                    </v-row>
                                </w-fieldset>
                            </v-col>
                            <v-col cols="12" md="7">
                                <v-container fluid>
                                    <iframe
                                        v-if="documentUrl"
                                        :src="documentUrl"
                                        width="100%"
                                        height="1200px"
                                        name="myIframe"
                                    />
                                </v-container>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card>
            </template>
        </w-app>
        <router-view/>
    </div>
</template>

<style scoped>

</style>